/**
 * Action bus — the seam between the ribbon (which only knows action *names*)
 * and the feature workstreams (which own the behaviour).
 *
 * Deliberately not a Zustand store: handlers are imperative side effects, and
 * routing them through state would make every dispatch a render. Multiple
 * handlers may claim the same name (e.g. the viewer and the inspector both
 * react to "view.reset"); all of them run, in registration order.
 */

import { useEffect } from "react";

export type ActionPayload = unknown;
export type ActionHandler = (payload?: ActionPayload) => void;

const handlers = new Map<string, Set<ActionHandler>>();

/** Registers `handler` for `name`. Returns the unregister function. */
export function registerAction(name: string, handler: ActionHandler): () => void {
  let set = handlers.get(name);
  if (!set) {
    set = new Set();
    handlers.set(name, set);
  }
  set.add(handler);
  return () => {
    set?.delete(handler);
    if (set && set.size === 0) handlers.delete(name);
  };
}

/**
 * Fires every handler registered for `name`. An unclaimed action is a warning,
 * not an exception: a half-built ribbon must not be able to crash the shell.
 * A throwing handler is likewise contained so one bad listener cannot stop the
 * others.
 */
export function dispatchAction(name: string, payload?: ActionPayload): void {
  const set = handlers.get(name);
  if (!set || set.size === 0) {
    console.warn(`[actions] no handler for "${name}"`, payload ?? "");
    return;
  }
  for (const handler of [...set]) {
    try {
      handler(payload);
    } catch (error) {
      console.error(`[actions] handler for "${name}" threw`, error);
    }
  }
}

/** True when at least one handler is listening — used to grey out nothing yet. */
export function hasActionHandler(name: string): boolean {
  return (handlers.get(name)?.size ?? 0) > 0;
}

/**
 * Component-scoped registration. The handler identity is intentionally part of
 * the dependency list: pass a `useCallback` result, or accept re-registration
 * on every render (cheap — it is a Set add/delete).
 */
export function useAction(name: string, handler: ActionHandler): void {
  useEffect(() => registerAction(name, handler), [name, handler]);
}
